import React from "react";
import type { PreconsultationAnswers } from "../types/preconsultation.types";

/**
 * The card this form reads answers back on, and the one screen that uses it
 * before anything has been answered.
 *
 * Two places draw a list of answers with a way back into the form: the review
 * screen at the end, and the basics step at the beginning — which opens on
 * what the practitioner already holds rather than on eight boxes a client
 * would only be typing their own name back into. They are the same card doing
 * the same job at two ends of the form, and a second copy of it is how two
 * screens quietly stop looking alike.
 */

/** What a question shows when it was left unanswered. Not "None" and not
 *  "N/A": those are claims about the person, and a blank is only a blank. */
export const UNANSWERED = "–";

export const value = (text: string): string => text.trim() || UNANSWERED;

export const list = (values: string[]): string =>
  values.length > 0 ? values.join(", ") : UNANSWERED;

export interface SummaryRow {
  label: string;
  text: string;
}

export interface SummaryCardProps {
  title: string;
  rows: SummaryRow[];
  onEdit: () => void;
  /**
   * How many answers stand side by side from `sm:` up.
   *
   * Set per card rather than one figure for all of them, because the design
   * fits the grid to what is in the card: four short answers across for the
   * names and the address, three where the answers are phrases, two where they
   * are sentences. A column count that ignored the content would either strand
   * "Software engineer" in a quarter-width column or leave a card of one-word
   * answers half empty.
   */
  columns?: 2 | 3 | 4;
  /**
   * What the Edit button is announced as, whole.
   *
   * Named for anybody who cannot see which card it sits on: the review screen
   * carries six of these, and six buttons all reading "Edit" is a list of six
   * identical links.
   */
  editLabel: string;
}

/** Written out rather than built, so Tailwind can see the class names. */
const COLUMNS: Record<2 | 3 | 4, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
};

export const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  rows,
  onEdit,
  editLabel,
  columns = 3,
}) => (
  <section className="rounded-[6px] border border-[#E0E1E6] bg-[#FCFCFD] p-5">
    <div className="flex items-baseline justify-between gap-4">
      <h2 className="text-[15px] font-medium text-[#1E1F24]">{title}</h2>
      <button
        type="button"
        onClick={onEdit}
        className="text-[14px] font-medium text-brand hover:underline"
      >
        <span aria-hidden="true">Edit</span>
        <span className="sr-only">{editLabel}</span>
      </button>
    </div>

    {/*
      Two readings of the same list, because a phone and a desktop want
      different things from it.

      On a phone it is a ledger: the label at the left, the answer at the
      right, a rule between each pair. That is the shape that survives a narrow
      column — the eye runs down the labels and across only where it stops.

      From `sm:` up it is the grid the design draws: the label above its answer
      and several of them across the card, no rules at all. There is width for
      three or four answers on a line there, and set as a ledger instead the
      card would be a stack of pairs with a hand's width of nothing between
      each label and its own value.
    */}
    <dl className={`mt-2 sm:mt-4 sm:grid sm:gap-x-6 sm:gap-y-5 ${COLUMNS[columns]}`}>
      {rows.map((row, index) => (
        <div
          key={row.label}
          className={
            "flex items-baseline justify-between gap-6 py-3 sm:block sm:py-0 " +
            (index < rows.length - 1 ? "border-b border-[#E0E1E6] sm:border-b-0" : "")
          }
        >
          <dt className="flex-shrink-0 text-[13px] sm:text-[14px] text-[#8B8D98]">
            {row.label}
          </dt>
          {/* Right-aligned on a phone and free to wrap: a long answer — four
              dishes, a full address — has to run onto a second line without
              pushing its own label off the row. Under the label from `sm:` up,
              where it has a column of its own to wrap inside. */}
          <dd className="min-w-0 text-right text-[14px] text-[#1E1F24] sm:mt-1 sm:text-left sm:text-[14px] font-semibold">
            {row.text}
          </dd>
        </div>
      ))}
    </dl>
  </section>
);

/**
 * The first step, where the practitioner already filled it in: what they hold
 * about this client, and one Edit.
 *
 * The client was added by somebody who had their name, their number and what
 * they came for. Opening a form on eight boxes already full of that — and
 * asking them to press Continue past every one — reads as a form that was not
 * expecting them, and is eight chances to change an answer nobody asked them
 * to change. So the step opens as a statement instead: here is what we have.
 * Continue takes it as read and moves straight on to step two; Edit opens the
 * same step's fields, where the two answers that are genuinely theirs to give
 * are the only two that can be typed into — see `BasicsStep`.
 *
 * A row the record has nothing for is a dash rather than a card left out. That
 * is the honest reading of it and the useful one: the dash against DOB is the
 * one thing this screen is actually asking for, and it sits directly beside
 * the Edit that opens it.
 */
export interface PreconsultationBasicsProps {
  answers: PreconsultationAnswers;
  onEdit: () => void;
}

/**
 * The seven answers both cards show about a person, in the design's order.
 *
 * Written once because the basics step and the review screen show the same
 * card about the same record, and the two drifting apart is exactly what a
 * reader notices — they see this card at the start of the form and again at
 * the end of it.
 *
 * The names are two answers here rather than one. They are two boxes behind
 * Edit and two columns in the design, and joining them for the card only to
 * split them again in the fields is a card that does not match what it opens.
 */
export const BASIC_ROWS = (
  answers: PreconsultationAnswers
): SummaryRow[] => [
  { label: "First name", text: value(answers.firstName) },
  { label: "Last name", text: value(answers.lastName) },
  { label: "Gender", text: value(answers.gender) },
  { label: "Goal", text: list(answers.goals) },
  { label: "Lifestyle", text: value(answers.lifestyle) },
  {
    label: "Phone",
    // The dialling code is drawn here rather than stored on the answer: the
    // field holds ten digits, and this is a reading of them.
    text: answers.phone ? `+91 ${answers.phone}` : UNANSWERED,
  },
  { label: "Email", text: value(answers.email) },
];

export const PreconsultationBasics: React.FC<PreconsultationBasicsProps> = ({
  answers,
  onEdit,
}) => (
  <SummaryCard
    title="Basic"
    onEdit={onEdit}
    editLabel="Edit your basic details"
    columns={4}
    rows={BASIC_ROWS(answers)}
  />
);

export default PreconsultationBasics;
